/**
 * How the business values a vehicle offered as collateral (finding #10, 15 Oct 2026): a share of its
 * retail value (from WeBuyCars), and it lends up to that recognised value. The share is a setting
 * (Settings > Loan, finding #29); the Credit Assessment Procedure Manual (7.1) sets 70%.
 */
export const DEFAULT_RECOGNISED_PERCENT = 70;

export const DEFAULT_RETAIL_VALUE_SOURCE = 'WeBuyCars';

/** The recognised share of the retail value, to the cent. */
export const discountedVehicleValue = (retailValue?: number | null, recognisedPercent: number = DEFAULT_RECOGNISED_PERCENT): number | undefined => {
  if (retailValue === undefined || retailValue === null || Number.isNaN(retailValue) || retailValue <= 0) return undefined;
  return Math.round(retailValue * recognisedPercent) / 100;
};

/** Loan account statuses where the customer still owes Tirhelo money. */
export const OPEN_ACCOUNT_STATUSES = ['ACTIVE', 'IN_ARREARS', 'DEFAULTED'];

export type ExistingExposure = { total: number; accounts: number };

/**
 * What the customer already owes Tirhelo on open loan accounts. The credit manual (7.1) measures the
 * security against Tirhelo's exposure after the loan: this plus the new loan (finding #10).
 */
export const existingExposure = (
  accounts?: { status?: string; totalOutstanding?: number; applicationId?: string }[] | null,
  excludeApplicationId?: string,
): ExistingExposure => {
  const open = (accounts || []).filter(a => a && OPEN_ACCOUNT_STATUSES.includes(String(a.status))
    && (!excludeApplicationId || a.applicationId !== excludeApplicationId) && (Number(a.totalOutstanding) || 0) > 0);
  const total = open.reduce((sum, a) => sum + (Number(a.totalOutstanding) || 0), 0);
  return { total: Math.round(total * 100) / 100, accounts: open.length };
};

/** A collateral item that is a vehicle: a retail value, make or registration number was captured. */
export const isVehicleCollateral = (c?: { retailValue?: number; make?: string; registrationNumber?: string; collateralType?: string } | null) =>
  !!c && (c.collateralType === 'VEHICLE' || (Number(c.retailValue) || 0) > 0 || !!c.make?.trim() || !!c.registrationNumber?.trim());
